// src/app/view-customers.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, TouchableOpacity, Alert } from 'react-native';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MdPhone, MdTextsms, MdEmail, MdAdd, MdRemove } from 'react-icons/md';
import API_BASE_URL from '../apiConfig.js';

// --- helpers ---
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// Adjust this if your header is taller/shorter
const RIGHT_PANEL_WIDTH = 420; // px
const RIGHT_PANEL_RIGHT_PADDING = RIGHT_PANEL_WIDTH + 40; // keep list clear of the panel
const CARD_HEIGHT = 110; // for alphabet rail scroll calculation

export default function ViewCustomers() {
  const navigate = useNavigate();
  const [userPermissions, setUserPermissions] = useState(null);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          // Check if user has customer permissions
          if (parsedPermissions?.customer_perm === 'none') {
            alert("You don't have permission to view customers. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // list state
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState('');
  const [loadingList, setLoadingList] = useState(false);

  // form state (merged from NewCustomer)
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city,   setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [zip,    setZip] = useState('');
  const [phone,  setPhone] = useState('');
  const [email,  setEmail] = useState('');
  const [notes,  setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  const scrollViewRef = useRef(null);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const loadCustomers = useCallback(async () => {
    try {
      setLoadingList(true);
      const token = getToken();
      const { data } = await axios.get(`${API_BASE_URL}/customers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const list = Array.isArray(data) ? data : data?.customers ?? [];
      setCustomers(list);
      // keep current search filter applied
      if (query.trim()) {
        const t = query.trim().toLowerCase();
        setFiltered(list.filter((c) => (c?.name ?? '').toLowerCase().includes(t)));
      } else {
        setFiltered(list);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoadingList(false);
    }
  }, [query]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleSearch = (text) => {
    setQuery(text);
    const t = text.trim().toLowerCase();
    if (!t) {
      setFiltered(customers);
      return;
    }
    const matches = customers.filter((c) => (c?.name ?? '').toLowerCase().includes(t));
    setFiltered(matches);
  };

  // web equivalents for Linking
  const callCustomer  = (p) => p && window.open(`tel:${p}`, '_self');
  const textCustomer  = (p) => p && window.open(`sms:${p}`, '_self');
  const emailCustomer = (e) => e && window.open(`mailto:${e}`, '_self');

  const scrollToLetter = (letter) => {
    const idx = filtered.findIndex((c) => (c?.name ?? '').toUpperCase().startsWith(letter));
    if (idx !== -1) {
      // Create a unique ID for the target customer card
      const targetId = `customer-card-${idx}`;
      
      // Use setTimeout to ensure DOM is rendered
      setTimeout(() => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  };

  // --- Save new customer (right panel) ---
  const resetForm = () => {
    setName(''); setAddress(''); setCity(''); setStateVal('');
    setZip(''); setPhone(''); setEmail(''); setNotes('');
    setIsNotesVisible(false);
  };

  const saveCustomer = async () => {
    if (!name.trim()) {
      (Alert?.alert ? Alert.alert('Missing info', 'Name is required.') : window.alert('Name is required.'));
      return;
    }
    try {
      setSaving(true);
      const token = getToken();
      const payload = {
        name: name.trim(),
        address: address.trim(),
        city: city.trim(),
        state: stateVal.trim(),
        zip: zip.trim(),
        phone: phone.trim(),
        email: email.trim(),
        notes,
      };
      await axios.post(`${API_BASE_URL}/customers`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      // reload list + reset form
      await loadCustomers();
      resetForm();

      (Alert?.alert ? Alert.alert('Success', 'Customer added!') : window.alert('Customer added!'));
    } catch (error) {
      console.error('Add customer failed:', error);
      const msg = error?.response?.data?.error || 'An error occurred while adding the customer.';
      (Alert?.alert ? Alert.alert('Error', msg) : window.alert(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[styles.root, { paddingRight: RIGHT_PANEL_RIGHT_PADDING }]}>
      {/* Header */}
      <View style={styles.headerContainer}>
        <Text style={styles.headerText}>All Customers</Text>
        <Text style={styles.totalCustomers}>
          {loadingList ? 'Loading…' : `${customers.length} Total Customers`}
        </Text>
      </View>

      {/* Left: Customers list */}
      <ScrollView
        ref={scrollViewRef}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.scrollContent}
        style={styles.scroll}
      >
        <TextInput
          style={styles.searchBox}
          placeholder="Search"
          placeholderTextColor="#999"
          value={query}
          onChangeText={handleSearch}
        />

        {query.length > 0 && filtered.length === 0 && (
          <Text style={styles.noMatch}>There is no customer by that name.</Text>
        )}
        {filtered.length === 0 && query.length === 0 && !loadingList && (
          <Text style={styles.noCustomers}>There are no customers to display.</Text>
        )}

        {filtered.map((customer, index) => (
          <TouchableOpacity
            key={customer.id || `customer-${index}`}
            id={`customer-card-${index}`}
            style={styles.card}
            onPress={() => navigate(`/customer-details?id=${encodeURIComponent(customer.id)}`)}
          >
            <Text style={styles.name}>{customer.name}</Text>
            <Text style={styles.address}>
              {`${customer.address || ''} ${customer.city || ''} ${customer.state || ''} ${customer.zip || ''}`.trim()}
            </Text>

            <View style={styles.iconRow}>
              {!!customer.phone && (
                <>
                  <TouchableOpacity onPress={() => callCustomer(customer.phone)} style={styles.iconButton}>
                    <MdPhone size={18} color="#fff" aria-hidden />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => textCustomer(customer.phone)} style={styles.iconButton}>
                    <MdTextsms size={18} color="#fff" aria-hidden />
                  </TouchableOpacity>
                </>
              )}
              {!!customer.email && (
                <TouchableOpacity onPress={() => emailCustomer(customer.email)} style={styles.iconButton}>
                  <MdEmail size={18} color="#fff" aria-hidden />
                </TouchableOpacity>
              )}
            </View>

            {!!customer.created && (
              <View style={styles.customerSinceContainer}>
                <Text style={styles.customerSinceDate}>
                  Customer Since {new Date(customer.created).toLocaleDateString('en-US')}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Alphabet rail (shifted left so it doesn't overlap the form) */}
      <View style={[styles.alphabetContainer, { right: RIGHT_PANEL_RIGHT_PADDING - 20 }]}>
        {alphabet.map((letter) => (
          <TouchableOpacity key={letter} onPress={() => scrollToLetter(letter)} style={styles.alphabetButton}>
            <Text style={styles.alphabetText}>{letter}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Right: Sticky / floating New Customer form */}
      <View style={styles.rightPanel}>
        <Text style={styles.formHeader}>Add New Customer</Text>

        <Text style={styles.label}>Name</Text>
        <TextInput style={styles.input} value={name} onChangeText={setName} />

        <Text style={styles.label}>Address</Text>
        <TextInput style={styles.input} value={address} onChangeText={setAddress} />

        <Text style={styles.label}>City</Text>
        <TextInput style={styles.input} value={city} onChangeText={setCity} />

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>State</Text>
            <TextInput style={styles.input} value={stateVal} onChangeText={setStateVal} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Zip</Text>
            <TextInput style={styles.input} value={zip} onChangeText={setZip} keyboardType="numeric" />
          </View>
        </View>

        <Text style={styles.label}>Phone</Text>
        <TextInput style={styles.input} value={phone} onChangeText={setPhone} keyboardType="phone-pad" />

        <Text style={styles.label}>Email</Text>
        <TextInput
          style={styles.input}
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
        />

        {!isNotesVisible ? (
          <TouchableOpacity style={styles.notesToggle} onPress={() => setIsNotesVisible(true)}>
            <MdAdd size={18} color="green" />
            <Text style={styles.notesToggleTextAdd}>Add Notes</Text>
          </TouchableOpacity>
        ) : (
          <>
            <TouchableOpacity style={styles.notesToggle} onPress={() => setIsNotesVisible(false)}>
              <MdRemove size={18} color="red" />
              <Text style={styles.notesToggleTextHide}>Hide Notes</Text>
            </TouchableOpacity>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea, { height: 150, textAlignVertical: 'top' }]}
              placeholder="Enter notes here..."
              multiline
              numberOfLines={6}
              onChangeText={setNotes}
              value={notes}
            />
          </>
        )}

        <TouchableOpacity style={[styles.saveButton, saving && { opacity: 0.7 }]} onPress={saveCustomer} disabled={saving}>
          <Text style={styles.saveButtonText}>{saving ? 'Saving…' : 'Add Customer'}</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.cancelButton} onPress={resetForm}>
          <Text style={styles.cancelButtonText}>Clear Form</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    paddingLeft: 10,
    paddingBottom: 50,
    paddingTop: 20,
  },

  // header
  headerContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  headerText: { fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  totalCustomers: { fontSize: 16, color: '#666' },

  // left column
  scroll: { flexGrow: 1 },
  scrollContent: { paddingBottom: 80, paddingRight: 20 },
  searchBox: { borderWidth: 1, borderColor: '#ccc', borderRadius: 6, padding: 10, marginBottom: 15 },
  noMatch: { fontStyle: 'italic', color: '#666', marginBottom: 10 },
  noCustomers: { textAlign: 'center', color: '#999', marginTop: 20, marginBottom: 10 },

  card: {
    backgroundColor: '#f9f9f9',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 2,
    elevation: 2,
    position: 'relative',
    minHeight: CARD_HEIGHT,
  },
  name: { fontWeight: 'bold', fontSize: 16, marginBottom: 4 },
  address: { fontSize: 14, color: '#444', marginBottom: 8 },

  iconRow: { flexDirection: 'row' },
  iconButton: { backgroundColor: 'green', padding: 6, borderRadius: 4, marginRight: 6, cursor: 'pointer' },

  alphabetContainer: {
    position: 'fixed',
    top: '18%',
    backgroundColor: 'transparent',
    alignItems: 'center',
    padding: 4,
    zIndex: 3,
  },
  alphabetButton: { paddingVertical: 3, paddingHorizontal: 4, borderRadius: 4, marginBottom: 1 },
  alphabetText: { fontSize: 12, color: '#007AFF' },

  customerSinceContainer: { position: 'absolute', bottom: 12, right: 10, alignItems: 'flex-end' },
  customerSinceDate: { fontSize: 12, color: '#666' },

  // right sticky panel
  rightPanel: {
    position: 'fixed',
    right: 16,
    top: 90, // adjust if your AppShell header is taller/shorter
    width: RIGHT_PANEL_WIDTH,
    maxHeight: '90vh',
    overflow: 'auto',
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    zIndex: 2,
    boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
  },
  formHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },

  label: { marginTop: 12, fontWeight: 'bold' },
  input: {
    borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 6, marginTop: 6, backgroundColor: '#fff',
  },
  textArea: {
    minHeight: 100,      // <-- increase to taste (e.g., 240)
    height: 100,         // RN Web tends to respect explicit height
    textAlignVertical: 'top',
    paddingTop: 10,
    paddingBottom: 10,
    lineHeight: 20,
  },

  notesToggle: { flexDirection: 'row', alignItems: 'center', marginTop: 14, gap: 6 },
  notesToggleTextAdd: { color: 'green', fontSize: 14, fontWeight: '600' },
  notesToggleTextHide: { color: 'red', fontSize: 14, fontWeight: '600' },

  saveButton: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center', marginTop: 18 },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  cancelButton: { backgroundColor: '#FF3B30', padding: 10, borderRadius: 6, alignItems: 'center', marginTop: 10 },
  cancelButtonText: { color: '#fff', fontSize: 15, fontWeight: 'bold' },
});
